// src/pages/Privacy.js
import React from 'react';

const Privacy = () => {
  return (
    <div className="page-container">
      <div className="last-updated">
        <strong>Last Updated:</strong> June 24, 2026
      </div>

      <h1>Privacy Policy</h1>

      <div className="highlight-box">
        <h3>Your Privacy Matters</h3>
        <p>
          At AI Vision App, we are committed to protecting your privacy and being transparent
          about how we collect, use, and protect your information. This policy explains our
          practices in simple terms.
        </p>
        <p style={{ marginTop: '12px' }}>
          This Privacy Policy works together with our <a href="/terms" style={{ color: '#8B5CF6', fontWeight: 'bold', textDecoration: 'none' }}>Terms of Service (EULA)</a>.
          Please read both documents to understand your rights and our practices.
        </p>
      </div>

      <div className="legal-section">
        <h2>1. Information We Collect</h2>

        <h3>1.1 Information You Provide</h3>
        <ul className="legal-list">
          <li><strong>Account Information:</strong> Email address, name, profile picture (when you sign in with Google or Apple via Firebase Authentication)</li>
          <li><strong>User Content:</strong> Photos you choose to analyze, saved detection results and favorites</li>
          <li><strong>Communications:</strong> Messages you send to our support team</li>
          <li><strong>Payment Information:</strong> Processed securely through App Store/Google Play (we do not store your payment details)</li>
          <li><strong>Subscription Data:</strong> Subscription status, plan type, purchase dates</li>
        </ul>

        <h3>1.2 Information We Collect Automatically</h3>
        <ul className="legal-list">
          <li><strong>Usage Data:</strong> How you interact with the app, features used, detection modes, scan counts</li>
          <li><strong>Device Information:</strong> Device type, operating system, app version, device identifiers (Android ID or iOS Identifier for Vendor)</li>
          <li><strong>Performance Data:</strong> Crash reports, error logs, app performance metrics via Firebase Crashlytics</li>
          <li><strong>Analytics Data:</strong> Aggregated usage statistics via Firebase Analytics</li>
        </ul>

        <h3>1.3 Camera and Image Processing</h3>
        <div className="warning-box">
          <h3>Important: Image Processing</h3>
          <p>
            <strong>Real-Time Detection:</strong> AI Vision uses your device camera to capture frames
            for real-time object detection. These frames are sent to the Google Gemini AI API for
            analysis. Camera frames are:
          </p>
          <ul className="legal-list">
            <li>Captured at low resolution (compressed) to minimize data usage</li>
            <li>Sent to Google Gemini API via encrypted connection for analysis</li>
            <li>Deleted from your device immediately after processing</li>
            <li>Not stored on our servers — only detection metadata (labels, confidence scores) is saved</li>
            <li>Never used for advertising or sold to third parties</li>
          </ul>
          <p style={{ marginTop: '12px' }}>
            <strong>Premium Cloud Backup:</strong> Premium subscribers who use the snapshot feature
            may have captured images uploaded to Firebase Storage for cloud backup. These images
            are stored securely and accessible only to the account owner.
          </p>
        </div>

        <h3>1.4 Device Identification for Usage Limits</h3>
        <div className="warning-box">
          <h3>Device Tracking for Fair Use</h3>
          <p>
            To enforce free-tier usage limits (5 scans per day), we collect a device identifier:
          </p>
          <ul className="legal-list">
            <li><strong>Android:</strong> Android ID (a unique device identifier)</li>
            <li><strong>iOS:</strong> Identifier for Vendor (identifierForVendor)</li>
          </ul>
          <p>
            This identifier is stored locally using secure storage (iOS Keychain / Android Keystore)
            and in our Firestore database to track daily scan counts per device. This prevents
            circumvention of usage limits through app reinstallation. The device identifier is
            not shared with third parties and is used solely for usage enforcement.
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>2. How We Use Your Information</h2>

        <h3>2.1 Core App Functionality</h3>
        <ul className="legal-list">
          <li>Provide AI-powered object detection, text recognition, barcode/QR code scanning, plant identification, food recognition, and landmark identification</li>
          <li>Save your detection history (object labels, confidence scores, mode, timestamps) and favorites</li>
          <li>Enforce free-tier usage limits (5 scans per day per device)</li>
          <li>Process subscription purchases and renewals</li>
          <li>Display advertisements to free-tier users via Google AdMob</li>
          <li>Provide text-to-speech feedback for detected objects</li>
          <li>Provide haptic and sound feedback during detection</li>
        </ul>

        <h3>2.2 App Improvement</h3>
        <ul className="legal-list">
          <li>Analyze usage patterns to improve app performance</li>
          <li>Fix bugs and technical issues via crash reports</li>
          <li>Develop new features and improvements</li>
          <li>Conduct research and analytics</li>
        </ul>

        <h3>2.3 Communication</h3>
        <ul className="legal-list">
          <li>Respond to your support requests and questions</li>
          <li>Send important app updates and security notifications</li>
          <li>Send subscription renewal reminders</li>
        </ul>

        <h3>2.4 Legal and Safety</h3>
        <ul className="legal-list">
          <li>Comply with legal obligations</li>
          <li>Enforce our Terms of Service</li>
          <li>Protect against fraud and abuse (including usage limit circumvention)</li>
          <li>Ensure platform security</li>
        </ul>
      </div>

      <div className="legal-section">
        <h2>3. Information Sharing and Disclosure</h2>

        <div className="highlight-box">
          <h3>Our Commitment</h3>
          <p>
            <strong>We do not sell, rent, or trade your personal information to third parties.</strong>
            We only share information in limited circumstances as described below.
          </p>
        </div>

        <h3>3.1 Service Providers</h3>
        <p>
          We use the following third-party service providers who are bound by strict
          data protection agreements:
        </p>
        <ul className="legal-list">
          <li><strong>Google Gemini AI API:</strong> Processes camera images for object detection and analysis. See <a href="https://ai.google.dev/terms" style={{ color: '#8B5CF6' }}>Google AI Terms</a></li>
          <li><strong>Firebase (Google Cloud):</strong> Authentication, Firestore database, Cloud Storage, Analytics, Crashlytics, and Cloud Functions. See <a href="https://firebase.google.com/support/privacy" style={{ color: '#8B5CF6' }}>Firebase Privacy</a></li>
          <li><strong>Google AdMob:</strong> Displays banner, interstitial, and rewarded ads to free-tier users. See <a href="https://policies.google.com/privacy" style={{ color: '#8B5CF6' }}>Google Privacy Policy</a></li>
          <li><strong>Apple/Google In-App Purchases:</strong> Subscription billing handled entirely by the App Store or Google Play Store</li>
        </ul>

        <h3>3.2 Legal Requirements</h3>
        <p>We may disclose information if required by law or to:</p>
        <ul className="legal-list">
          <li>Comply with legal processes or government requests</li>
          <li>Protect our rights, property, or safety</li>
          <li>Protect the rights, property, or safety of our users</li>
          <li>Investigate fraud or security issues</li>
          <li>Enforce our Terms of Service</li>
        </ul>

        <h3>3.3 Business Transfers</h3>
        <p>
          If AI Vision App is acquired or merged with another company, your information
          may be transferred as part of that transaction. You will be notified via email
          and/or prominent notice in the app at least 30 days before any such change.
        </p>
      </div>

      <div className="legal-section">
        <h2>4. Data Security</h2>

        <div className="highlight-box">
          <h3>Security Measures</h3>
          <p>
            We implement industry-standard security measures to protect your information:
          </p>
        </div>

        <ul className="legal-list">
          <li><strong>Encryption:</strong> All data is encrypted in transit (TLS) and at rest</li>
          <li><strong>Secure Device Storage:</strong> Device identifiers stored using Flutter Secure Storage (iOS Keychain / Android Keystore)</li>
          <li><strong>Firebase Security Rules:</strong> Firestore and Storage access restricted to authenticated users viewing their own data</li>
          <li><strong>Access Controls:</strong> Limited access on a need-to-know basis</li>
          <li><strong>Secure Infrastructure:</strong> Using Google Cloud's secure data centers</li>
          <li><strong>Data Minimization:</strong> Detection history stores only metadata (labels, confidence, timestamps) — not images for free users</li>
        </ul>

        <div className="warning-box">
          <h3>Important Security Notice</h3>
          <p>
            While we implement robust security measures, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot guarantee absolute
            security but continuously work to protect your information using industry best practices.
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>5. Your Rights and Choices</h2>

        <h3>5.1 Access and Control</h3>
        <p>You have the following rights regarding your personal information:</p>
        <ul className="legal-list">
          <li><strong>Access:</strong> View your detection history and account information in the app</li>
          <li><strong>Delete:</strong> Clear individual history items or your entire detection history</li>
          <li><strong>Export:</strong> Export your detection data (PDF, CSV formats available)</li>
          <li><strong>Account Deletion:</strong> Delete your account and all associated data</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
        </ul>

        <p>
          <strong>To exercise these rights:</strong> Use in-app settings or
          email us at <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a>
        </p>

        <h3>5.2 App Permissions</h3>
        <p>You can control app permissions in your device settings:</p>
        <ul className="legal-list">
          <li><strong>Camera:</strong> Required for real-time object detection and barcode scanning. Core features won't work without this.</li>
          <li><strong>Microphone:</strong> Optional, for voice commands (e.g., "capture", "switch camera", "toggle flash")</li>
          <li><strong>Photos:</strong> Optional, for selecting images from your library for analysis</li>
          <li><strong>Notifications:</strong> Optional, for app updates</li>
        </ul>

        <h3>5.3 Haptic and Sound Settings</h3>
        <p>
          You can enable or disable haptic feedback and sound notifications in the app settings.
          These preferences are stored locally on your device.
        </p>
      </div>

      <div className="legal-section">
        <h2>6. Children's Privacy</h2>

        <div className="warning-box">
          <h3>Age Restrictions (COPPA Compliance)</h3>
          <p>
            AI Vision App is not intended for children under 13 years of age. We do not
            knowingly collect personal information from children under 13.
          </p>
        </div>

        <p>
          If we discover we have collected information from a child under 13, we will
          delete the information immediately and terminate the account.
        </p>

        <p>
          <strong>Parents:</strong> If you believe your child has provided information to us,
          please contact us immediately at <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a>
        </p>
      </div>

      <div className="legal-section">
        <h2>7. Advertising</h2>
        <p>
          Free-tier users see advertisements served by Google AdMob, including banner ads,
          interstitial ads (between detections), and optional rewarded ads (for extra scans).
          AdMob may collect device identifiers and usage data for ad personalization.
        </p>
        <p>
          Premium subscribers enjoy a completely ad-free experience. We do not sell your
          personal data to advertisers.
        </p>
      </div>

      <div className="legal-section">
        <h2>8. Data Retention</h2>

        <p>We retain your information for as long as necessary to provide our services:</p>

        <div className="highlight-box">
          <ul className="legal-list">
            <li><strong>Account Data:</strong> Retained while your account is active + 30 days after deletion request</li>
            <li><strong>Detection History:</strong> Retained until you delete it or close your account</li>
            <li><strong>Device Usage Data:</strong> Daily scan counts reset daily; device identifiers retained for fair-use enforcement</li>
            <li><strong>Analytics:</strong> Anonymized and aggregated, retained for up to 2 years</li>
            <li><strong>Crash Reports:</strong> Retained for 90 days</li>
            <li><strong>Camera Frames:</strong> Deleted immediately after AI processing (not stored)</li>
          </ul>
        </div>
      </div>

      <div className="legal-section">
        <h2>9. International Data Transfers</h2>

        <p>
          AI Vision App operates globally. Your information may be processed and stored in
          countries other than your own, including the United States, through Google Cloud
          infrastructure. We ensure appropriate safeguards are in place for international
          data transfers.
        </p>
      </div>

      <div className="legal-section">
        <h2>10. California Privacy Rights (CCPA/CPRA)</h2>

        <p>
          If you are a California resident, you have specific rights under the CCPA/CPRA:
        </p>
        <ul className="legal-list">
          <li><strong>Right to Know:</strong> Request information about data we've collected</li>
          <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
          <li><strong>Right to Opt-Out:</strong> Opt out of sale of personal information (we don't sell data)</li>
          <li><strong>Right to Non-Discrimination:</strong> We won't discriminate for exercising your rights</li>
        </ul>
        <p>
          To exercise these rights, email: <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a> with subject "California Privacy Rights"
        </p>
      </div>

      <div className="legal-section">
        <h2>11. European Privacy Rights (GDPR)</h2>

        <p>
          If you are in the EEA, UK, or Switzerland, you have additional rights under GDPR
          including data portability, right to rectification, and the right to lodge a complaint
          with your local data protection authority.
        </p>
        <p>
          <strong>Contact:</strong> <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a>
        </p>
      </div>

      <div className="legal-section">
        <h2>12. Changes to This Policy</h2>

        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          communicated through in-app notifications and by updating the "Last Updated" date.
          Continued use after changes indicates acceptance of the updated policy.
        </p>
      </div>

      <div className="legal-section">
        <h2>13. Contact Us</h2>

        <div className="highlight-box">
          <p>
            <strong>Email:</strong> <a href="mailto:cyborguser2020@gmail.com" style={{ color: '#8B5CF6' }}>cyborguser2020@gmail.com</a>
            <br />
            <strong>Support:</strong> <a href="https://aivisionsapp.web.app/support" style={{ color: '#8B5CF6' }}>https://aivisionsapp.web.app/support</a>
            <br />
            <strong>Terms of Service:</strong> <a href="https://aivisionsapp.web.app/terms" style={{ color: '#8B5CF6' }}>https://aivisionsapp.web.app/terms</a>
            <br />
            <strong>Response Time:</strong> Within 72 hours
          </p>
        </div>
      </div>

      <div className="legal-section">
        <h2>14. Your Acceptance</h2>

        <p>
          By using AI Vision App, you acknowledge that you have read and understood this
          Privacy Policy and agree to its terms. This Privacy Policy is part of and
          incorporated into our <a href="/terms" style={{ color: '#8B5CF6', fontWeight: 'bold' }}>Terms of Service (EULA)</a>.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
